<template>
  <form class="form" novalidate @submit.prevent="handleSubmit">
    <FormRow v-if="organization.type == OrganizationType.Corporate">
      <FormFiles
          v-model="organization.logo"
          :default-selected-files="organization.logo != null ? [organization.logo] : []"
          :label="t('organization.logo')"
          :possiblesTypes="['image/png', 'image/jpg', 'image/jpeg', 'image/webp']"
          :tooltip="t(`organization.logoFormat`)"
          name="logo"
          @change="onImageChange($event)"
          @update:modelValue="onImageChange($event)"
      />
    </FormRow>
    <FormRow v-if="memberStore.hasRole(Role.Admin)" :withThreeColumns="true">
      <FormReadonlyField v-model="organization.crmId"
                         :label="t('global.crmId')" name="crmId"/>
      <FormReadonlyField v-model="organizationType"
                         :label="t('organization.type')" name="type"/>
      <FormReadonlyField v-model="organization.chapter"
                         :label="t('organization.chapter')"
                         name="chapter"/>
    </FormRow>
    <FormRow v-else :withThreeColumns="true">
      <FormReadonlyField v-model="organizationType"
                         :label="t('organization.type')" name="type"/>
      <FormReadonlyField v-model="organization.chapter"
                         :label="t('organization.chapter')"
                         name="chapter"/>
      <FormReadonlyField v-model="organization.memberNumber"
                         :label="t('organization.memberNumber')"
                         name="memberNumber"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormReadonlyField v-model="organization.subscription.startAt"
                         :label="t('organization.subscriptionStartAt')"
                         name="subscriptionStartAt"/>
      <FormReadonlyField v-model="organization.subscription.endAt"
                         :label="t('organization.subscriptionEndAt')"
                         name="subscriptionEndAt"/>
      <FormInput :ref="addFormInputRef"
                 v-model="organization.enterpriseNumber"
                 :label="t('organization.enterpriseNumber')"
                 name="enterpriseNumber"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="organization.apartment"
                 :label="t('global.apartment')"
                 :rules="[]"
                 name="apartment"
                 type="number"
                 @validated="handleValidation"/>
      <FormInput :ref="addFormInputRef"
                 v-model="organization.street"
                 :label="t('global.street')"
                 :list="'address-list'"
                 name="street"
                 type="text"
                 @validated="handleValidation"
                 @update:modelValue="handleInput">
        <template v-slot:before-input>
          <datalist id="address-list">
            <option v-for="address in addressOptions" :id="address.id" :key="address.id">
              {{ address.text }}
            </option>
          </datalist>
        </template>
      </FormInput>
      <FormInput :ref="addFormInputRef"
                 v-model="organization.city"
                 :label="t('global.city')"
                 name="city"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="organization.zipCode"
                 :label="t('global.zipCode')"
                 :rules="[required, mustMatchZipCodeFormat]"
                 name="zipCode"
                 type="zip"
                 @validated="handleValidation"/>
      <div class="form__duo-fields">
        <FormInput :ref="addFormInputRef"
                   v-model="organization.phoneNumber"
                   :label="t('global.phoneNumber')"
                   :rules="[required, mustMatchPhoneNumberFormat]"
                   name="phoneNumber"
                   type="tel"
                   @validated="handleValidation"/>
        <FormInput :ref="addFormInputRef"
                   v-model="organization.phoneExtension"
                   :label="t('global.phoneExtension')"
                   :rules="[]"
                   name="phoneExtension"
                   type="number"
                   @validated="handleValidation"/>
      </div>
      <FormInput :ref="addFormInputRef"
                 v-model="organization.email"
                 :label="t('global.email')"
                 name="email"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormMultiSelect v-if="organization.type === OrganizationType.Corporate"
                       :ref="addFormInputRef"
                       :label="t('organization.activityAreas')"
                       :max-items="5"
                       :modelValue="activityAreas"
                       :options="ActivityAreaHelper.activityAreaFormOptions()"
                       name="activityAreas"
                       @onOptionChange="onActivityAreaChange"/>
      <FormInput v-if="organization.type === OrganizationType.Corporate"
                 :ref="addFormInputRef"
                 v-model="organization.yearsOfExperience"
                 :label="t('organization.yearsOfExperience')"
                 name="yearsOfExperience"
                 type="number"
                 @validated="handleValidation"/>
      <FormInput v-if="organization.type === OrganizationType.Corporate"
                 :ref="addFormInputRef"
                 v-model="organization.website"
                 :label="t('organization.website')"
                 :rules="[]"
                 name="website"
                 type="text"
                 @validated="handleValidation"/>
      <FormInput v-if="organization.type !== OrganizationType.Corporate"
                 :ref="addFormInputRef"
                 v-model="organization.numberOfUnits"
                 :label="t('organization.numberOfUnits')"
                 :rules="[required, min(2)]"
                 name="numberOfUnits"
                 type="number"
                 @validated="handleValidation"/>
      <FormReadonlyField v-if="organization.type === OrganizationType.RegularIndividual && organization.syndicate?.name"
                         v-model="organization.syndicate.name"
                         :label="t('organization.syndicate')"
                         name="syndicateName"/>
    </FormRow>
    <FormRow v-if="organization.type === OrganizationType.Corporate && memberStore.hasRole(Role.Admin)"
             :center-fields="true">
      <FormCheckbox
          :ref="addFormInputRef"
          v-model="organization.hasContingencyFundCertification"
          :label="t('organization.hasContingencyFundCertification')"
          name="isPublished"
          @validated="handleValidation"
      />
    </FormRow>
    <FormTextEditor
        v-if="organization.type == OrganizationType.Corporate"
        :ref="addFormInputRef"
        v-model="organization.descriptionFr"
        :label="t('organization.descriptionFr')"
        :rules="[]"
        name="description-fr"
        @validated="handleValidation"
    />
    <FormTextEditor
        v-if="organization.type == OrganizationType.Corporate"
        :ref="addFormInputRef"
        v-model="organization.descriptionEn"
        :label="t('organization.descriptionEn')"
        :rules="[]"
        name="description-en"
        @validated="handleValidation"
    />
    <button class="form__submit btn btn--fullscreen">{{ t('global.save') }}</button>
  </form>
</template>

<script lang="ts" setup>
import {computed, ref} from "vue"
import {useI18n} from "vue3-i18n"
import {notifyError} from "@/notify";
import {useMemberStore} from "@/stores/memberStore";
import {useAddressService} from "@/inversify.config"
import {min, mustMatchPhoneNumberFormat, mustMatchZipCodeFormat, required} from "@/validation/rules";
import {FormOption, OrganizationType, Role} from "@/types";
import {IFullOrganization} from "@/types/entities/organization";
import {Status} from "@/validation";
import FormRow from "@/components/forms/FormRow.vue";
import FormInput from "@/components/forms/FormInput.vue"
import FormReadonlyField from "@/components/forms/FormReadonlyField.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import {IAddress} from "@/types/responses";
import {IFilterAddressesRequest, IGetAddressRequest} from "@/types/requests";
import FormMultiSelect from "@/components/forms/FormMultiSelect.vue";
import {ActivityAreaHelper} from "@/types/activityArea";
import FormTextEditor from "@/components/forms/FormTextEditor.vue";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";

// eslint-disable-next-line no-undef
const props = defineProps<{
  modelValue: IFullOrganization
}>()

// eslint-disable-next-line
const emit = defineEmits<{
  // states that the event has to be called 'update:modelValue'
  (event: "update:modelValue", value: IFullOrganization): void;
  (event: "onImageChange", image: File): void;
  (event: "submit", value: IFullOrganization): void;
}>();

const {t, getLocale} = useI18n()
const memberStore = useMemberStore()
const addressService = useAddressService()

const addressOptions = ref<IAddress[]>([])
const activityAreas = ref<FormOption[]>(props.modelValue.activityAreas
    ? ActivityAreaHelper.convertActivityAreaKeysToFormOptions(props.modelValue.activityAreas)
    : [])
const organization = ref<IFullOrganization>(props.modelValue)

let organizationType = computed(() => {
  if (!organization.value)
    return;
  let type = organization.value.type.makeFirstLetterLowercase();
  return t(`organization.types.${type}`)
})

const formInputs = ref<(typeof FormInput)[]>([])
const inputValidationStatuses: any = {}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref))
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function onImageChange(event: any) {
  const files = event?.target?.files
  if (!files)
    return;
  emit('onImageChange', files.length === 0 ? undefined : files[0])
}

function onActivityAreaChange(newOptions: FormOption[]) {
  activityAreas.value = newOptions
  organization.value.activityAreas = activityAreas.value.map(x => x.name)
}

async function handleInput(inputValue: string) {
  let selectedOptions = addressOptions.value.filter(x => x.text == inputValue)
  if (selectedOptions.length > 0) {
    let request = {
      id: selectedOptions[0].id,
      language: getLocale()
    } as IGetAddressRequest
    let response = await addressService.getAddress(request)
    organization.value.street = response.detailedAddress.street
    organization.value.city = response.detailedAddress.city
    organization.value.zipCode = response.detailedAddress.zipCode.replace(' ', '')
    return;
  }
  await loadMatchingAddresses(inputValue)
}

async function loadMatchingAddresses(inputValue: string) {
  let request = {
    street: inputValue,
    language: getLocale()
  } as IFilterAddressesRequest
  let addresses = await addressService.filterAddresses(request)
  addressOptions.value = addresses.matchingAddresses
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  }
  emit('submit', organization.value as IFullOrganization)
}
</script>
